import { useContext, useState } from 'react';
import { Tooltip } from '@fluentui/react-components';
import {
  Attitude,
  EmployeeSkill,
  EmployeeSkillsByCategoryDTO,
  Level,
} from 'src/tabs/models/SkillsInterfaces';
import {
  SkillsEditPopupOverlay,
  SkillsMiddleContainer,
  RadioGroupStyled,
  FinishingActionsContainer,
  AccordionPanelLeftColumnTextStyle,
  AccordionPanelRightColumnTextStyle,
  EditLiStyled,
  EditMiddleWrapper,
  EditSelectedInfoWrapper,
  EditSelectedTitleStyle,
  EditSelectedInfoStyle,
  EditDividerStyle,
  EditStatusWrapper,
  EditMiddleWrapperGrid,
  SpinnerContainer,
  EditMiddleWrapperSubGrid,
} from './styles/SkillsEditPopup.styled';
import {
  AddSkillDivider,
  HeaderDivider,
  LabelStyled,
  RadioButtonStyled,
  RadioButtonWrapper,
  SelectorContainer,
  SkillsAddBackdrop,
  SkillsBottomContainer,
  SkillsDiscardButton,
  SkillsSaveButton,
  SmallAlertIcon,
} from '../common/styles/common.styled';
import { usePatchSkill } from '../hooks/usePatchSkill';
import { formatSkillDate } from 'src/utils/dateFns/formatDate';
import { SkillsStatusIndicator } from '../SkillsStatusIndicator/SkillsStatusIndicator';
import { SkillTopContainerMobile } from '../common/mobile/SkillTopContainerMobile';
import { SkillBottomContainer } from './mobile/SkillBottomContainer';
import { ErrorsMessageContainer } from '../common/components/ErrorsMessageContainer';
import { ErrorsMessageContainerMobile } from '../common/mobile/ErrorsMessageContainerMobile';
import { SkillTopContainer } from '../common/components/SkillTopContainer';
import { LoadingSpinner } from 'src/components/common/LoadingSpinner/LoadingSpinner';
import {
  SKILL_ADVANCED_MASTERY_LABEL,
  SKILL_EDIT_VALIDATION_MESSAGE,
  SKILL_ENTRY_LEVEL_MASTERY_LABEL,
  SKILL_INTERMEDIATE_MASTERY_LABEL,
} from 'src/tabs/constants/Skills.constants';
import { TeamsFxContext } from 'src/context/TeamsContext';

interface SkillsEditPopupProps {
  onClose: () => void;
  skillToEditDTO: EmployeeSkillsByCategoryDTO;
  skillToEditCategory: string;
  getSkills: () => void;
  selectedSkillToEdit: EmployeeSkill;
  isLoadingEmployeeSkill: boolean;
}

export const SkillsEditPopup = ({
  onClose,
  selectedSkillToEdit,
  skillToEditDTO,
  getSkills,
  isLoadingEmployeeSkill,
}: SkillsEditPopupProps) => {
  const { isMobile } = useContext(TeamsFxContext);
  const { patchSkill } = usePatchSkill();
  const skillToEdit = skillToEditDTO.employeeSkills[0];
  const levelCode =
    +skillToEdit.level?.label === 1
      ? '759590000'
      : +skillToEdit.level?.label === 2
        ? '759590001'
        : '759590002';
  const attitudeCode =
    skillToEdit.attitude?.label === 'Positive'
      ? '759590000'
      : skillToEdit.attitude?.label === 'Neutral'
        ? '759590001'
        : '759590002';
  const [level, setLevel] = useState(null);
  const [attitude, setAttitude] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const onClear = () => {
    setLevel(null);
    setAttitude(null);
    setErrorMessage('');
  };

  const triggerRadioButton = (optionValue) => {
    const radioBtn = document.querySelector(
      `[value="${optionValue}"]`
    ) as HTMLInputElement;
    if (radioBtn) {
      radioBtn.click();
    }
  };

  const editSkillHandler = async () => {
    if (
      (level === levelCode && attitude === attitudeCode) ||
      (!level && !attitude)
    ) {
      if (!level && !attitude) {
        setErrorMessage(
          'Please change the Level and Attitude selection to continue.'
        );
      } else if (level === levelCode && attitude === attitudeCode) {
        setErrorMessage(
          'Level or Attitude must be different than the old ones.'
        );
      }
    } else if (!level || !attitude) {
      setErrorMessage(
        'Please change the Level or Attitude selection to continue.'
      );
    } else if (level !== null && attitude !== null) {
      await patchSkill(skillToEdit.id, level, attitude);
      getSkills();
      onClose();
    }
  };

  if (isLoadingEmployeeSkill) {
    return (
      <>
        {!isMobile && <SkillsAddBackdrop onClick={onClose} />}
        <SkillsEditPopupOverlay>
          {isMobile ? (
            <SkillTopContainerMobile
              onClear={onClear}
              onClose={onClose}
              title="Request skill change"
              displayCondition={level !== null || attitude !== null}
            />
          ) : (
            <SkillTopContainer onClose={onClose} title="Request skill change" />
          )}
          <HeaderDivider />
          <SpinnerContainer isMobile={isMobile}>
            <LoadingSpinner />
          </SpinnerContainer>
        </SkillsEditPopupOverlay>
      </>
    );
  }

  return (
    <>
      {!isMobile && <SkillsAddBackdrop onClick={onClose} />}
      <SkillsEditPopupOverlay>
        {isMobile ? (
          <SkillTopContainerMobile
            onClear={onClear}
            onClose={onClose}
            title="Request skill change"
            displayCondition={level !== null || attitude !== null}
          />
        ) : (
          <SkillTopContainer onClose={onClose} title="Request skill change" />
        )}
        <HeaderDivider />
        <SkillsMiddleContainer>
          {errorMessage &&
            (!level ||
              !attitude ||
              (level === levelCode && attitude === attitudeCode)) && (
              <ErrorsMessageContainerMobile
                errorMessage={errorMessage}
                setErrorMessage={setErrorMessage}
              />
            )}
          <EditSelectedInfoWrapper>
            <EditSelectedTitleStyle>Category</EditSelectedTitleStyle>
            <EditSelectedInfoStyle>
              {skillToEditDTO.category.label}
            </EditSelectedInfoStyle>
          </EditSelectedInfoWrapper>
          <EditSelectedInfoWrapper>
            <EditSelectedTitleStyle>Skill</EditSelectedTitleStyle>
            <EditSelectedInfoStyle>
              {skillToEdit.skill.name}
            </EditSelectedInfoStyle>
          </EditSelectedInfoWrapper>
          {!isMobile && <EditDividerStyle />}
          <EditMiddleWrapper>
            <EditMiddleWrapperGrid>
              <EditStatusWrapper gridArea="validated-header">
                <SkillsStatusIndicator status="1" />
                <h1>Validated changes</h1>
              </EditStatusWrapper>
              <EditStatusWrapper gridArea="requested-header">
                <SkillsStatusIndicator status="2" />
                <h1>Requested changes</h1>
              </EditStatusWrapper>
              <EditMiddleWrapperSubGrid>
                <EditLiStyled gridArea="validated-date-label">
                  <AccordionPanelLeftColumnTextStyle>
                    Date
                  </AccordionPanelLeftColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled gridArea="validated-level-label">
                  <AccordionPanelLeftColumnTextStyle>
                    Level
                  </AccordionPanelLeftColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled gridArea="validated-attitude-label">
                  <AccordionPanelLeftColumnTextStyle>
                    Attitude
                  </AccordionPanelLeftColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled gridArea="validated-by-label">
                  <AccordionPanelLeftColumnTextStyle>
                    By
                  </AccordionPanelLeftColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled gridArea="validated-date-value">
                  <AccordionPanelRightColumnTextStyle>
                    {skillToEdit.validatedDate
                      ? formatSkillDate(skillToEdit.validatedDate)
                      : '-'}
                  </AccordionPanelRightColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled gridArea="validated-level-value">
                  <AccordionPanelRightColumnTextStyle>
                    {selectedSkillToEdit.validatedDate &&
                    skillToEdit.level?.label
                      ? skillToEdit.level?.label
                      : '-'}
                  </AccordionPanelRightColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled gridArea="validated-attitude-value">
                  <AccordionPanelRightColumnTextStyle>
                    {selectedSkillToEdit.validatedDate &&
                    skillToEdit.attitude?.label
                      ? skillToEdit.attitude?.label
                      : '-'}
                  </AccordionPanelRightColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled gridArea="validated-by-value">
                  <AccordionPanelRightColumnTextStyle>
                    {selectedSkillToEdit.validatedDate &&
                    selectedSkillToEdit?.validatedBy
                      ? selectedSkillToEdit?.validatedBy
                      : '-'}
                  </AccordionPanelRightColumnTextStyle>
                </EditLiStyled>

                <EditLiStyled
                  gridArea="requested-date-label"
                  id="requested-date-label"
                >
                  <AccordionPanelLeftColumnTextStyle>
                    Date
                  </AccordionPanelLeftColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled
                  gridArea="requested-level-label"
                  id="requested-level-label"
                >
                  <AccordionPanelLeftColumnTextStyle>
                    Level
                  </AccordionPanelLeftColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled
                  gridArea="requested-attitude-label"
                  id="requested-attitude-label"
                >
                  <AccordionPanelLeftColumnTextStyle>
                    Attitude
                  </AccordionPanelLeftColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled gridArea="requested-date-value">
                  <AccordionPanelRightColumnTextStyle>
                    {skillToEdit.requestedDate
                      ? formatSkillDate(skillToEdit.requestedDate)
                      : '-'}
                  </AccordionPanelRightColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled gridArea="requested-level-value">
                  <AccordionPanelRightColumnTextStyle>
                    {skillToEdit.requestedLevel?.label || '-'}
                  </AccordionPanelRightColumnTextStyle>
                </EditLiStyled>
                <EditLiStyled gridArea="requested-attitude-value">
                  <AccordionPanelRightColumnTextStyle>
                    {skillToEdit.requestedAttitude?.label || '-'}
                  </AccordionPanelRightColumnTextStyle>
                </EditLiStyled>
              </EditMiddleWrapperSubGrid>
            </EditMiddleWrapperGrid>
          </EditMiddleWrapper>
        </SkillsMiddleContainer>
        {isMobile ? (
          <SkillBottomContainer
            attitude={attitude}
            errorMessage={errorMessage}
            level={level}
            setAttitude={setAttitude}
            setLevel={setLevel}
            setErrorMessage={setErrorMessage}
            attitudeCode={attitudeCode}
            levelCode={levelCode}
            triggerRadioButton={triggerRadioButton}
          />
        ) : (
          <SkillsBottomContainer>
            {errorMessage &&
              (!level ||
                !attitude ||
                (level === levelCode && attitude === attitudeCode)) && (
                <ErrorsMessageContainer errorMessage={errorMessage} />
              )}
            <SelectorContainer>
              {errorMessage && !level && <SmallAlertIcon />}
              <LabelStyled required>Level</LabelStyled>
              <RadioGroupStyled
                layout="horizontal"
                value={level}
                onChange={(_, newData) => {
                  setLevel(newData.value);
                  if (
                    attitude === attitudeCode &&
                    newData.value === levelCode
                  ) {
                    setErrorMessage(SKILL_EDIT_VALIDATION_MESSAGE);
                  }
                }}
              >
                <Tooltip
                  content={SKILL_ENTRY_LEVEL_MASTERY_LABEL}
                  relationship="label"
                >
                  <RadioButtonWrapper>
                    <RadioButtonStyled
                      errorStyle={errorMessage !== '' && !level}
                      value={Level.LEVEL1}
                      label="Level 1"
                    />
                  </RadioButtonWrapper>
                </Tooltip>
                <Tooltip
                  content={SKILL_INTERMEDIATE_MASTERY_LABEL}
                  relationship="label"
                >
                  <RadioButtonWrapper>
                    <RadioButtonStyled
                      errorStyle={errorMessage !== '' && !level}
                      value={Level.LEVEL2}
                      label="Level 2"
                    />
                  </RadioButtonWrapper>
                </Tooltip>
                <Tooltip
                  content={SKILL_ADVANCED_MASTERY_LABEL}
                  relationship="label"
                >
                  <RadioButtonWrapper>
                    <RadioButtonStyled
                      errorStyle={errorMessage !== '' && !level}
                      value={Level.LEVEL3}
                      label="Level 3"
                    />
                  </RadioButtonWrapper>
                </Tooltip>
              </RadioGroupStyled>
            </SelectorContainer>
            <AddSkillDivider />
            <SelectorContainer>
              {errorMessage && !attitude && <SmallAlertIcon />}
              <LabelStyled required>Attitude</LabelStyled>
              <RadioGroupStyled
                layout="horizontal"
                value={attitude}
                onChange={(_, newData) => {
                  setAttitude(newData.value);
                  if (newData.value === attitudeCode && level === levelCode) {
                    setErrorMessage(SKILL_EDIT_VALIDATION_MESSAGE);
                  }
                }}
              >
                <RadioButtonWrapper>
                  <RadioButtonStyled
                    errorStyle={errorMessage !== '' && !attitude}
                    value={Attitude.POSITIVE}
                    label="Positive"
                  />
                </RadioButtonWrapper>
                <RadioButtonWrapper>
                  <RadioButtonStyled
                    errorStyle={errorMessage !== '' && !attitude}
                    value={Attitude.NEUTRAL}
                    label="Neutral"
                  />
                </RadioButtonWrapper>
                <RadioButtonWrapper>
                  <RadioButtonStyled
                    errorStyle={errorMessage !== '' && !attitude}
                    value={Attitude.NEGATIVE}
                    label="Negative"
                  />
                </RadioButtonWrapper>
              </RadioGroupStyled>
            </SelectorContainer>
          </SkillsBottomContainer>
        )}
        <FinishingActionsContainer>
          <SkillsDiscardButton
            onClick={() => {
              onClose();
              setLevel('');
              setAttitude('');
            }}
          >
            Discard
          </SkillsDiscardButton>
          <SkillsSaveButton onClick={editSkillHandler}>Submit</SkillsSaveButton>
        </FinishingActionsContainer>
      </SkillsEditPopupOverlay>
    </>
  );
};
